import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  PlusIcon,
  ArrowPathIcon,
  ClockIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import ScraperV2CreateSessionModal from '../components/ScraperV2CreateSessionModal';
import ScraperV2CategoriesTab from '../components/ScraperV2CategoriesTab';
import ScraperV2LinkDomainsTab from '../components/ScraperV2LinkDomainsTab';
import ScraperV2UsernamesTab from '../components/ScraperV2UsernamesTab';
import ScraperV2SourcePerformanceTab from '../components/ScraperV2SourcePerformanceTab';
import ScraperV2AutoSourcePanel from '../components/ScraperV2AutoSourcePanel';
import ScraperV2AddAccountsPanel from '../components/ScraperV2AddAccountsPanel';
import {
  ScraperV2OverallProgress,
  ScraperV2PipelineStages,
  CollapsibleDrawer,
  ScraperV2SourceProgressList,
  ScraperV2SourceCountryCheckBar,
  ScraperV2SourceLinkCheckBar,
  ScraperV2AddSourcesPanel,
  ScraperV2CountryFilterPanel,
  partitionSourceProgress,
} from '../components/ScraperV2PipelinePanel';

/** Live scrapes only — stopped sheets paint from cache and skip the recount. */
const STATS_POLL_MS = 12_000;
const INVENTORY_POLL_MS = 15_000;

function isLiveSessionStatus(status) {
  return status === 'running' || status === 'paused';
}

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

function cacheUpdatedLabel(sc) {
  const recount = sc?.recount;
  if (recount?.status === 'running') {
    const seen = Number(recount.rowsSeen) || 0;
    const chunk = Number(recount.batches) || 0;
    return `Recounting… ${fmt(seen)} raw${chunk ? ` · ${chunk} chunks` : ''}`;
  }
  if (recount?.status === 'failed') {
    return `Recount failed${recount.error ? ` — ${recount.error}` : ''}`;
  }
  if (!sc?.updatedAt) return 'Cached counts — no recount';
  const d = new Date(sc.updatedAt);
  if (Number.isNaN(d.getTime())) return 'Cached counts — no recount';
  return `Cached counts · ${d.toLocaleString()} — Refresh to recount`;
}

function AccountStatsPanel({ title, stats }) {
  if (!stats || !stats.total) return null;
  return (
    <div className="rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-800/40 p-4">
      <div className="flex items-center gap-1.5 mb-3 text-xs text-slate-600 dark:text-slate-300">
        <ClockIcon className="h-3.5 w-3.5" />
        <span className="font-semibold">{title}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Total</div>
          <div className="font-semibold tabular-nums">{fmt(stats.total)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-emerald-600">Available</div>
          <div className="font-semibold text-emerald-600 tabular-nums">{fmt(stats.available)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-orange-600">Rate-limited</div>
          <div className="font-semibold text-orange-600 tabular-nums">{fmt(stats.rateLimited)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-red-600">Blocked</div>
          <div className="font-semibold text-red-600 tabular-nums">{fmt(stats.blocked)}</div>
        </div>
      </div>
    </div>
  );
}

const ScraperV2 = () => {
  const { socket } = useSocket();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [inventoryRows, setInventoryRows] = useState([]);
  const [inventoryTotals, setInventoryTotals] = useState(null);
  const [selectedRawSessions, setSelectedRawSessions] = useState(() => new Set());
  const [selectedFilteredSessions, setSelectedFilteredSessions] = useState(() => new Set());
  const [inventoryDeleting, setInventoryDeleting] = useState(false);
  const [tab, setTab] = useState('sessions');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [checkers, setCheckers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [countryDrawerOpen, setCountryDrawerOpen] = useState(false);
  const [sourceDrawerOpen, setSourceDrawerOpen] = useState(false);
  /**
   * In-flight follower-total repair, scoped to the session it was started for
   * so switching sessions never shows another session's button as busy.
   * @type {[{sessionId: string, queuedAt: number, busy: boolean}|null, Function]}
   */
  const [backfillRun, setBackfillRun] = useState(null);
  const [workspaceIndexStatus, setWorkspaceIndexStatus] = useState(null);
  const [workspaceIndexBusy, setWorkspaceIndexBusy] = useState(false);
  const selectedIdRef = useRef(null);

  const canUseActiveCheckers = user?.role === 'admin' || user?.features?.canUseActiveCheckers;

  const loadWorkspaceIndexes = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await api.get('/scraper-v2/workspace-indexes');
      if (res.data?.success) setWorkspaceIndexStatus(res.data);
    } catch (_) { /* non-fatal */ }
  }, [user?.role]);

  useEffect(() => {
    loadWorkspaceIndexes();
  }, [loadWorkspaceIndexes]);

  const runWorkspaceIndexMigration = async () => {
    setWorkspaceIndexBusy(true);
    try {
      const res = await api.post('/scraper-v2/workspace-indexes/migrate');
      if (res.data?.success) {
        setWorkspaceIndexStatus(res.data);
        toast.success(res.data.needed ? 'Migration finished — some indexes still pending' : 'Workspace indexes ready');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Workspace index migration failed');
    } finally {
      setWorkspaceIndexBusy(false);
      loadWorkspaceIndexes();
    }
  };

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.get('/scraper-v2/sessions');
      if (res.data.success) setSessions(res.data.sessions || []);
    } catch (err) {
      if (err.response?.status === 503) {
        toast.error('Scraper V2 is not enabled yet (SCRAPER_V2_ENABLED=false)');
      } else {
        toast.error(err.response?.data?.error || 'Failed to load sessions');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const [acc, chk] = await Promise.all([
        api.get('/scraper/accounts'),
        api.get('/scraper/active-checker-accounts'),
      ]);
      if (acc.data.success) {
        setAccounts((acc.data.accounts || []).filter((a) => a.hasValidSession));
      }
      if (chk.data.success) setCheckers(chk.data.accounts || []);
    } catch (_) { /* non-fatal */ }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/scraper-v2/categories');
      if (res.data.success) setCategories(res.data.categories || []);
    } catch (_) {
      /* non-fatal — Add Sources still allows creating a new category inline */
    }
  }, []);

  const loadDetail = useCallback(async (sessionId, { refreshStats = false, sources = false } = {}) => {
    if (!sessionId) return;
    try {
      const params = new URLSearchParams();
      if (refreshStats) params.set('refresh', '1');
      else if (!sources) params.set('slim', '1');
      const qs = params.toString();
      const requests = [
        api.get(`/scraper-v2/sessions/${sessionId}/stats${qs ? `?${qs}` : ''}`),
      ];
      if (sources) requests.push(api.get(`/scraper-v2/sessions/${sessionId}`));
      const [s, d] = await Promise.all(requests);
      if (selectedIdRef.current !== sessionId) return;
      if (d?.data?.success) setDetail(d.data.session);
      if (s.data.success) {
        const next = s.data.statsCache;
        setStats((prev) => {
          if (!next) return prev;
          if (prev && (!next.sourceProgress || !sources)) {
            return {
              ...prev,
              ...next,
              sourceProgress: next.sourceProgress || prev.sourceProgress,
              rawBySource: next.rawBySource || prev.rawBySource,
            };
          }
          return next;
        });
        if (next) {
          setSessions((prev) => prev.map((row) => (
            row.sessionId === sessionId
              ? { ...row, statsCache: { ...row.statsCache, ...next } }
              : row
          )));
        }
      }
    } catch (err) {
      if (selectedIdRef.current !== sessionId) return;
      toast.error(err.response?.data?.error || 'Failed to load session');
    }
  }, []);

  const selectSession = useCallback((row) => {
    if (!row?.sessionId) return;
    selectedIdRef.current = row.sessionId;
    setSelectedId(row.sessionId);
    setDetail(row);
    setStats(row.statsCache || null);
    setSourceDrawerOpen(false);
  }, []);

  /**
   * Look up profile follower totals for sources that never got one. Queued to
   * the driver, so counts appear over the next polls rather than immediately.
   */
  const handleBackfillFollowers = useCallback(async (sessionId) => {
    if (!sessionId) return;
    setBackfillRun({ sessionId, queuedAt: 0, busy: true });
    try {
      const res = await api.post(`/scraper-v2/sessions/${sessionId}/backfill-followers-count`, {});
      if (res.data?.queued || res.data?.alreadyRunning) {
        toast.success(res.data.message || 'Looking up follower totals…');
        // Stay busy until the driver reports 'running'; between the enqueue and
        // the worker picking the job up, nothing else marks this in progress.
        setBackfillRun({ sessionId, queuedAt: Date.now(), busy: false });
      } else {
        toast(res.data?.message || 'Nothing to fix');
        setBackfillRun(null);
      }
      await loadDetail(sessionId, { refreshStats: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start follower total lookup');
      setBackfillRun(null);
    }
  }, [loadDetail]);

  const loadInventory = useCallback(async () => {
    try {
      const [bySession, filteredPreview] = await Promise.all([
        api.get('/scraper-v2/inventory/by-session'),
        api.get('/scraper-v2/inventory?limit=50'),
      ]);
      if (bySession.data.success) {
        setInventoryRows(bySession.data.rows || []);
        setInventoryTotals(bySession.data.totals || null);
      }
      if (filteredPreview.data.success) setInventory(filteredPreview.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load inventory');
    }
  }, []);

  const toggleRawSession = (sessionId) => {
    setSelectedRawSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const toggleFilteredSession = (sessionId) => {
    setSelectedFilteredSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const selectAllRaw = (checked) => {
    if (!checked) {
      setSelectedRawSessions(new Set());
      return;
    }
    setSelectedRawSessions(new Set(
      inventoryRows.filter((r) => r.rawCount > 0).map((r) => r.sessionId)
    ));
  };

  const selectAllFiltered = (checked) => {
    if (!checked) {
      setSelectedFilteredSessions(new Set());
      return;
    }
    setSelectedFilteredSessions(new Set(
      inventoryRows.filter((r) => r.filteredCount > 0).map((r) => r.sessionId)
    ));
  };

  const deleteSelectedInventory = async () => {
    const sessionIds = new Set([...selectedRawSessions, ...selectedFilteredSessions]);
    if (!sessionIds.size) {
      toast.error('Select raw and/or filtered data to delete');
      return;
    }

    const items = [...sessionIds].map((sessionId) => ({
      sessionId,
      deleteRaw: selectedRawSessions.has(sessionId),
      deleteFiltered: selectedFilteredSessions.has(sessionId),
    })).filter((item) => item.deleteRaw || item.deleteFiltered);

    const rawTotal = items.filter((i) => i.deleteRaw).length;
    const filteredTotal = items.filter((i) => i.deleteFiltered).length;
    if (!window.confirm(
      `Delete selected data for ${items.length} session(s)?\n\n`
      + `${rawTotal} session(s): raw pipeline data\n`
      + `${filteredTotal} session(s): stored (filtered) data\n\n`
      + 'Running sessions will be stopped. This cannot be undone.'
    )) {
      return;
    }

    setInventoryDeleting(true);
    try {
      const res = await api.post('/scraper-v2/inventory/delete', { items });
      if (res.data.success) {
        toast.success(res.data.message || 'Data deleted');
        setSelectedRawSessions(new Set());
        setSelectedFilteredSessions(new Set());
        await loadInventory();
        await loadSessions();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete data');
    } finally {
      setInventoryDeleting(false);
    }
  };

  useEffect(() => {
    loadSessions();
    loadAccounts();
    loadCategories();
  }, [loadSessions, loadAccounts, loadCategories]);

  const selectedStatus = sessions.find((s) => s.sessionId === selectedId)?.status;

  useEffect(() => {
    selectedIdRef.current = selectedId;
    if (!selectedId) {
      setDetail(null);
      setStats(null);
    }
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || !sourceDrawerOpen) return undefined;
    loadDetail(selectedId, { sources: true });
    return undefined;
  }, [selectedId, sourceDrawerOpen, loadDetail]);

  useEffect(() => {
    if (!socket || !user) return;
    const sessionUpdateHandler = (payload) => {
      if (!payload?.sessionId) return;
      if (selectedId === payload.sessionId) {
        loadDetail(payload.sessionId, {
          refreshStats: isLiveSessionStatus(selectedStatus),
        });
      }
      loadSessions();
    };
    const statsProgressHandler = (payload) => {
      if (!payload?.sessionId || payload.sessionId !== selectedId || !payload.statsCache) return;
      const next = payload.statsCache;
      setStats((prev) => ({
        ...(prev || {}),
        ...next,
        sourceProgress: prev?.sourceProgress,
        rawBySource: prev?.rawBySource,
      }));
      setSessions((prev) => prev.map((row) => (
        row.sessionId === payload.sessionId
          ? { ...row, statsCache: { ...row.statsCache, ...next } }
          : row
      )));
    };
    socket.on('scraper-v2-session-update', sessionUpdateHandler);
    socket.on('scraper-v2-stats-progress', statsProgressHandler);
    return () => {
      socket.off('scraper-v2-session-update', sessionUpdateHandler);
      socket.off('scraper-v2-stats-progress', statsProgressHandler);
    };
  }, [socket, user, selectedId, selectedStatus, loadDetail, loadSessions]);

  useEffect(() => {
    if (tab !== 'sessions' || !selectedId) return undefined;
    if (!isLiveSessionStatus(selectedStatus)) return undefined;

    let pageVisible = document.visibilityState !== 'hidden';
    const onVisibility = () => {
      pageVisible = document.visibilityState !== 'hidden';
    };
    document.addEventListener('visibilitychange', onVisibility);

    const t = setInterval(() => {
      if (!pageVisible) return;
      loadSessions();
      loadDetail(selectedId);
    }, STATS_POLL_MS);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tab, selectedId, selectedStatus, loadDetail, loadSessions]);

  const recounting = stats?.recount?.status === 'running';

  useEffect(() => {
    if (tab !== 'sessions' || !selectedId || !recounting) return undefined;
    const t = setInterval(() => loadDetail(selectedId), 1_500);
    return () => clearInterval(t);
  }, [tab, selectedId, recounting, loadDetail]);

  useEffect(() => {
    if (tab !== 'inventory') return undefined;

    let pageVisible = document.visibilityState !== 'hidden';
    const onVisibility = () => {
      pageVisible = document.visibilityState !== 'hidden';
    };
    document.addEventListener('visibilitychange', onVisibility);

    const tick = () => {
      if (!pageVisible) return;
      loadInventory();
    };

    tick();
    const t = setInterval(tick, INVENTORY_POLL_MS);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tab, loadInventory]);

  const sessionAction = async (sessionId, action) => {
    try {
      await api.post(`/scraper-v2/sessions/${sessionId}/${action}`);
      toast.success(`Session ${action}`);
      await loadSessions();
      if (selectedId === sessionId) await loadDetail(sessionId);
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action}`);
    }
  };

  const deleteSession = async (session, { dataOnly = false } = {}) => {
    if (!session?.sessionId) return;
    const label = session.name || session.sessionId;
    const actionLabel = dataOnly
      ? 'wipe all scraped data for'
      : 'permanently delete';
    const detail = dataOnly
      ? 'Raw followers and filtered inventory will be removed. The session stays paused with sources intact — use Restart scrape when done.'
      : 'Raw followers, filtered inventory, and the session record will all be removed.';
    if (!window.confirm(
      `${actionLabel.charAt(0).toUpperCase()}${actionLabel.slice(1)} "${label}"?\n\n${detail}\n\nThis cannot be undone.`
    )) {
      return;
    }

    try {
      const url = dataOnly
        ? `/scraper-v2/sessions/${encodeURIComponent(session.sessionId)}?dataOnly=1`
        : `/scraper-v2/sessions/${encodeURIComponent(session.sessionId)}`;
      const res = await api.delete(url);
      if (res.data.success) {
        toast.success(
          res.data.message
          || `Removed ${fmt(res.data.rawDeleted)} raw and ${fmt(res.data.filteredDeleted)} filtered rows`
        );
        if (dataOnly && selectedId === session.sessionId) {
          await loadSessions();
          await loadDetail(session.sessionId, { refreshStats: true });
        } else {
          selectedIdRef.current = null;
          setSelectedId(null);
          setDetail(null);
          setStats(null);
          await loadSessions();
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete session data');
    }
  };

  const handleSourcesAdded = async () => {
    if (!selectedId) return;
    await loadSessions();
    await loadDetail(selectedId, { refreshStats: true });
    await loadCategories();
    setSourceDrawerOpen(true);
  };

  const handleAccountsAdded = async () => {
    if (!selectedId) return;
    await loadSessions();
    await loadDetail(selectedId, { refreshStats: true });
    await loadAccounts();
  };

  const handleCreate = async (payload) => {
    if (!payload.accountIds?.length) {
      toast.error('Select at least one scrape account');
      return;
    }
    if (!payload.activeCheckerAccountIds?.length) {
      toast.error('Select at least one checker account');
      return;
    }
    if (payload.filters?.filterCountryWhitelist && !payload.filters?.allowedCountries?.length) {
      toast.error('Add at least one country to the whitelist');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/scraper-v2/sessions', payload);
      if (res.data.success) {
        const { skippedGlobal = 0 } = res.data;
        toast.success(
          skippedGlobal > 0
            ? `Session created (${skippedGlobal} username(s) skipped — already scraped globally)`
            : 'Session created'
        );
        setShowCreate(false);
        await loadSessions();
        selectSession(res.data.session);
        setTab('sessions');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create session');
    } finally {
      setSubmitting(false);
    }
  };

  const selected = sessions.find((s) => s.sessionId === selectedId);
  const sc = stats || selected?.statsCache || {};
  const sessionFilters = detail?.filters || selected?.filters || {};
  const sourceProgress = Array.isArray(sc.sourceProgress) ? sc.sourceProgress : [];
  const sourcesTotal = Number(sc.sourcesTotal) || detail?.sourceUsernames?.length || 0;
  const sourcesComplete = Number(sc.sourcesComplete) || 0;
  const sourceBuckets = partitionSourceProgress(sourceProgress);

  // Hold the button disabled across the enqueue → worker-pickup gap, only for
  // the session the repair was started on. Cleared as soon as the driver
  // reports state for *this* run (hence the timestamp compare — a leftover
  // 'done' from an earlier run must not clear it), and capped by a grace
  // window in case no worker ever claims the job.
  const BACKFILL_QUEUE_GRACE_MS = 60_000;
  const backfillForThisSession = backfillRun?.sessionId === selected?.sessionId ? backfillRun : null;
  const backfillReportedAt = sc.followersBackfill
    ? new Date(sc.followersBackfill.startedAt || sc.followersBackfill.finishedAt || 0).getTime()
    : 0;
  const backfillBusy = Boolean(backfillForThisSession?.busy)
    || (
      Number(backfillForThisSession?.queuedAt) > 0
      && backfillReportedAt < backfillForThisSession.queuedAt
      && Date.now() - backfillForThisSession.queuedAt < BACKFILL_QUEUE_GRACE_MS
    );

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Scraper V2</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Honest query-based progress · dedicated scrape DB · scalable workers
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <PlusIcon className="w-5 h-5" />
          New session
        </button>
      </div>

      {user?.role === 'admin' && workspaceIndexStatus?.needed && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-amber-900 dark:text-amber-100">
            <div className="font-semibold">Workspace indexes not ready</div>
            <p className="text-xs mt-0.5 opacity-80">
              Railway also runs this on deploy. Until it finishes, Clean sheet can collide with current data
              {workspaceIndexStatus.running ? ' — already running.' : '.'}
            </p>
          </div>
          <button
            type="button"
            disabled={workspaceIndexBusy || workspaceIndexStatus.running}
            onClick={runWorkspaceIndexMigration}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-50"
          >
            {workspaceIndexBusy || workspaceIndexStatus.running ? 'Migrating…' : 'Run index migration'}
          </button>
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {['sessions', 'categories', 'inventory', 'usernames', 'source-performance', 'link-domains'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px ${
              tab === t
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t === 'usernames' ? 'Usernames'
              : t === 'source-performance' ? 'Source performance'
              : t === 'categories' ? 'Categories'
              : t === 'link-domains' ? 'Link Domains'
              : t}
          </button>
        ))}
      </div>

      {tab === 'categories' && <ScraperV2CategoriesTab />}

      {tab === 'link-domains' && (
        <ScraperV2LinkDomainsTab sessionId={selectedId} />
      )}

      {tab === 'sessions' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-2">
            {loading && <p className="text-sm text-gray-500">Loading…</p>}
            {!loading && sessions.length === 0 && (
              <p className="text-sm text-gray-500">No sessions yet.</p>
            )}
            {sessions.map((s) => (
              <div
                key={s.sessionId}
                className={`rounded-xl border transition ${
                  selectedId === s.sessionId
                    ? 'border-indigo-500 bg-indigo-500/5'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <button
                  type="button"
                  onClick={() => selectSession(s)}
                  className="w-full text-left p-4 pb-2"
                >
                  <div className="font-medium text-gray-900 dark:text-white">
                    {s.name}
                    {s.isolated ? (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                        Clean sheet
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(s.categories?.length ? s.categories.join(', ') : s.tag) || '—'} · {s.status}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    Filtered: {fmt(s.statsCache?.filteredReady)} · Raw: {fmt(s.statsCache?.rawUnique)}
                  </div>
                </button>
                <div className="px-4 pb-3 flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteSession(s, { dataOnly: true }); }}
                    className="text-xs px-2 py-1 rounded border border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                  >
                    Wipe
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteSession(s, { dataOnly: false }); }}
                    className="text-xs px-2 py-1 rounded border border-red-500/50 text-red-600 dark:text-red-400 hover:bg-red-500/10"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2 space-y-4">
            {selected ? (
              <>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {selected.status === 'running' && (
                      <button type="button" onClick={() => sessionAction(selected.sessionId, 'pause')} className="btn-secondary inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm">
                        <PauseIcon className="w-4 h-4" /> Pause
                      </button>
                    )}
                    {(selected.status === 'paused' || selected.status === 'stopped') && (
                      <button type="button" onClick={() => sessionAction(selected.sessionId, 'resume')} className="btn-secondary inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm">
                        <PlayIcon className="w-4 h-4" /> {selected.status === 'stopped' ? 'Restart scrape' : 'Resume'}
                      </button>
                    )}
                    {['running', 'paused', 'stopped'].includes(selected.status) && (
                      <button type="button" onClick={() => sessionAction(selected.sessionId, 'stop')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm">
                        <StopIcon className="w-4 h-4" /> Stop
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={recounting}
                      onClick={() => loadDetail(selected.sessionId, { refreshStats: true })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50"
                    >
                      <ArrowPathIcon className={`w-4 h-4${recounting ? ' animate-spin' : ''}`} />
                      {recounting ? 'Recounting' : 'Refresh'}
                    </button>
                    <span className="self-center text-[11px] text-gray-500">
                      {cacheUpdatedLabel(sc)}
                    </span>
                  </div>

                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 dark:bg-amber-950/20 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300 mb-2">
                      Data management
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      Remove scraped raw rows and/or filtered inventory for this session.
                      {' '}You can also bulk-delete from the Inventory tab.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => deleteSession(selected, { dataOnly: true })}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 shadow-sm"
                      >
                        <TrashIcon className="w-4 h-4" /> Wipe data
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteSession(selected, { dataOnly: false })}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 shadow-sm"
                      >
                        <TrashIcon className="w-4 h-4" /> Delete session
                      </button>
                    </div>
                  </div>
                </div>

                {(selected.status === 'paused' || selected.status === 'stopped') && (
                  <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 dark:bg-blue-950/20 p-3 text-sm text-blue-900 dark:text-blue-200">
                    {selected.status === 'stopped'
                      ? 'Session is stopped — click Restart scrape to run again (sources and accounts are unchanged).'
                      : 'Session is paused — click Resume to continue scraping.'}
                  </div>
                )}

                <ScraperV2AddSourcesPanel
                  sessionId={selected.sessionId}
                  sessionStatus={selected.status}
                  categories={categories}
                  onAdded={handleSourcesAdded}
                />

                <ScraperV2AutoSourcePanel
                  sessionId={selected.sessionId}
                  sessionStatus={selected.status}
                  categories={categories}
                  filters={sessionFilters}
                  onFiltersUpdated={() => loadDetail(selected.sessionId, { refreshStats: true })}
                  onSourcesAdded={handleSourcesAdded}
                />

                <ScraperV2OverallProgress
                  stats={sc}
                  sourceProgress={sourceProgress}
                  backfill={sc.followersBackfill}
                  backfillBusy={backfillBusy}
                  onBackfillFollowers={() => handleBackfillFollowers(selected.sessionId)}
                />

                <ScraperV2PipelineStages stats={sc} sourceProgress={sourceProgress} filters={sessionFilters} />

                {(sc.accountStats?.scrape?.total > 0 || sc.accountStats?.checker?.total > 0) && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <AccountStatsPanel title="Scrape accounts (this session)" stats={sc.accountStats?.scrape} />
                    <AccountStatsPanel title="Checker accounts (this session)" stats={sc.accountStats?.checker} />
                  </div>
                )}

                <ScraperV2AddAccountsPanel
                  sessionId={selected.sessionId}
                  sessionStatus={selected.status}
                  sessionAccountIds={detail?.accountIds || selected.accountIds || []}
                  accounts={accounts}
                  onAdded={handleAccountsAdded}
                />

                <ScraperV2CountryFilterPanel
                  sessionId={selected.sessionId}
                  filters={sessionFilters}
                  onUpdated={() => loadDetail(selected.sessionId, { refreshStats: true })}
                />

                {sc.byCountry && Object.keys(sc.byCountry).length > 0 && (
                  <CollapsibleDrawer
                    open={countryDrawerOpen}
                    onToggle={() => setCountryDrawerOpen((v) => !v)}
                    title={`Country breakdown (${Object.keys(sc.byCountry).length} countries)`}
                    subtitle="Passed country check only — rejected excluded"
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                      {Object.entries(sc.byCountry)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 24)
                        .map(([code, count]) => (
                          <div key={code} className="flex justify-between px-2 py-1 rounded bg-gray-50 dark:bg-gray-800">
                            <span>{code}</span>
                            <span className="font-mono">{fmt(count)}</span>
                          </div>
                        ))}
                    </div>
                  </CollapsibleDrawer>
                )}

                {(sourceProgress.length > 0 || sourcesTotal > 0) && (
                  <CollapsibleDrawer
                    open={sourceDrawerOpen}
                    onToggle={() => setSourceDrawerOpen((v) => !v)}
                    title={sourceProgress.length
                      ? `Source progress (${sourceBuckets.running.length} running · ${sourceBuckets.finished.length} finished · ${sourceBuckets.blocked.length} blocked)`
                      : `Source progress (${fmt(sourcesComplete)} / ${fmt(sourcesTotal)} finished)`}
                    subtitle="Crawl % = API rows fetched · ingest % = unique new rows from source · finished = no longer fetching"
                  >
                    {sourceProgress.length > 0 ? (
                      <>
                        <ScraperV2SourceCountryCheckBar
                          sessionId={selected.sessionId}
                          backfill={sc.sourceCountryBackfill}
                          onStarted={() => loadDetail(selected.sessionId, { refreshStats: true })}
                        />
                        <ScraperV2SourceLinkCheckBar
                          sessionId={selected.sessionId}
                          backfill={sc.sourceLinkBackfill}
                          onStarted={() => loadDetail(selected.sessionId, { refreshStats: true })}
                        />
                        <ScraperV2SourceProgressList
                          sourceProgress={sourceProgress}
                          sessionId={selected.sessionId}
                          accountStats={sc.accountStats?.scrape}
                          onRunCountryCheck={() => loadDetail(selected.sessionId, { refreshStats: true })}
                          onRemoveSource={() => loadDetail(selected.sessionId, { refreshStats: true })}
                        />
                      </>
                    ) : (
                      <p className="text-xs text-gray-500">Loading per-source progress…</p>
                    )}
                  </CollapsibleDrawer>
                )}

                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4 text-sm text-gray-500">
                  Link VA agents to the global <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">scrape_v2_pool</code> source. Scrape dedup is global (already in raw); each agent&apos;s follow model handles its own follow ledger separately.
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-sm">Select a session to view pipeline stats.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'inventory' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="font-medium">Data by session</span>
                {inventoryTotals && (
                  <p className="text-xs text-gray-500 mt-1">
                    {fmt(inventoryTotals.raw)} raw · {fmt(inventoryTotals.filtered)} stored
                    {' '}({fmt(inventoryTotals.filteredUnclaimed)} unclaimed)
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => loadInventory()}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm"
                >
                  <ArrowPathIcon className="w-4 h-4" /> Refresh
                </button>
                <button
                  type="button"
                  disabled={inventoryDeleting || (!selectedRawSessions.size && !selectedFilteredSessions.size)}
                  onClick={deleteSelectedInventory}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm disabled:opacity-50"
                >
                  <TrashIcon className="w-4 h-4" />
                  {inventoryDeleting ? 'Deleting…' : 'Delete selected'}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={inventoryRows.length > 0 && inventoryRows.every((r) => !r.rawCount || selectedRawSessions.has(r.sessionId))}
                          onChange={(e) => selectAllRaw(e.target.checked)}
                        />
                        Raw
                      </label>
                    </th>
                    <th className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={inventoryRows.length > 0 && inventoryRows.every((r) => !r.filteredCount || selectedFilteredSessions.has(r.sessionId))}
                          onChange={(e) => selectAllFiltered(e.target.checked)}
                        />
                        Stored
                      </label>
                    </th>
                    <th className="px-4 py-3">Session</th>
                    <th className="px-4 py-3">Tag</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Raw rows</th>
                    <th className="px-4 py-3 text-right">Stored rows</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {inventoryRows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        No session data yet.
                      </td>
                    </tr>
                  )}
                  {inventoryRows.map((row) => (
                    <tr key={row.sessionId} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          disabled={!row.rawCount}
                          checked={selectedRawSessions.has(row.sessionId)}
                          onChange={() => toggleRawSession(row.sessionId)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          disabled={!row.filteredCount}
                          checked={selectedFilteredSessions.has(row.sessionId)}
                          onChange={() => toggleFilteredSession(row.sessionId)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{row.name}</div>
                        <div className="text-xs text-gray-500 font-mono truncate max-w-[220px]">{row.sessionId}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.tag}</td>
                      <td className="px-4 py-3 capitalize text-gray-500">{row.status}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmt(row.rawCount)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {fmt(row.filteredCount)}
                        {row.filteredUnclaimed > 0 && row.filteredUnclaimed !== row.filteredCount && (
                          <span className="text-xs text-gray-500 block">
                            {fmt(row.filteredUnclaimed)} unclaimed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {inventory && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between">
                <span className="font-medium">Stored preview (unclaimed, latest 50)</span>
                <span className="text-sm text-gray-500">{fmt(inventory.total)} total unclaimed</span>
              </div>
              {inventory.byCountry && (
                <div className="p-4 grid grid-cols-4 gap-2 text-sm border-b border-gray-100 dark:border-gray-800">
                  {Object.entries(inventory.byCountry).slice(0, 12).map(([c, n]) => (
                    <div key={c}>{c}: {fmt(n)}</div>
                  ))}
                </div>
              )}
              <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-96 overflow-auto">
                {(inventory.rows || []).map((r) => (
                  <div key={`${r.username}-${r.sessionId}`} className="px-4 py-2 text-sm flex justify-between">
                    <span>@{r.username}</span>
                    <span className="text-gray-500">{r.country || '—'} · {r.tag}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'usernames' && (
        <ScraperV2UsernamesTab canUseActiveCheckers={canUseActiveCheckers} />
      )}

      {tab === 'source-performance' && (
        <ScraperV2SourcePerformanceTab />
      )}

      <ScraperV2CreateSessionModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        submitting={submitting}
        accounts={accounts}
        checkers={checkers}
        canUseActiveCheckers={canUseActiveCheckers}
      />
    </div>
  );
};

export default ScraperV2;
